import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useLang } from "../LanguageContext";
import CamperSolarBlogDe from "../content/CamperSolarBlog.de";
import CamperSolarBlogEn from "../content/CamperSolarBlog.en";

const YtIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="resource-icon">
    <path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-1.96C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 1.96A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33A2.78 2.78 0 0 0 3.4 19.1C5.12 19.56 12 19.56 12 19.56s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-1.95 29 29 0 0 0 .46-5.25 29 29 0 0 0-.46-5.33z"/>
    <polygon points="9.75 15.02 15.5 11.75 9.75 8.48 9.75 15.02"/>
  </svg>
);

function CamperTools() {
  const { t, lang } = useLang();
  const [formData, setFormData] = useState({
    cableLength: 1,
    voltageDrop: 1,
    material: "copper",
    crossSection: 0.75,
    voltage: 12.8,
    current: 1,
  });

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: parseFloat(value) || value }));
  };

  const results = useMemo(() => {
    const { voltage, current, cableLength, voltageDrop, material, crossSection } = formData;
    const resistivity = material === "copper" ? 0.0178 : 0.0282;
    const resistance = (resistivity * cableLength * 2) / crossSection;
    const actualVoltageDrop = ((current * resistance) / voltage) * 100;
    const temperatureRaise =
      (resistivity * Math.pow(current, 2)) /
      (Math.PI * (38 / 1000) * crossSection * (5 + 4 * 0.85 * 0.0000000567 * Math.pow(294, 3)));
    const power = voltage * current;
    const meetsRequirement = actualVoltageDrop <= voltageDrop;
    return { power, actualVoltageDrop, meetsRequirement, temperatureRaise, cableTemp: 20 + temperatureRaise };
  }, [formData]);

  const Field = ({ id, label, min, max, step }) => (
    <div className="calc-field">
      <label htmlFor={id}>{label}</label>
      <input type="number" id={id} value={formData[id]} onChange={handleInputChange}
        min={min} max={max} step={step} className="input-field" />
    </div>
  );

  const Select = ({ id, label, options }) => (
    <div className="calc-field">
      <label htmlFor={id}>{label}</label>
      <select id={id} value={formData[id]} onChange={handleInputChange} className="select-field">
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );

  const BlogContent = lang === "de" ? CamperSolarBlogDe : CamperSolarBlogEn;

  return (
    <div className="cablecalc">
      <div className="page-section-header">
        <span className="section-label">{t("camperTools.label")}</span>
        <div className="header">{t("camperTools.title")}</div>
        {t("camperTools.intro").map((p, i) => (
          <p key={i} className="section-intro-inner">{p}</p>
        ))}
      </div>

      <div className="camper-cards">
        <div className="tool-card camper-inputs-card">
          <p className="calc-desc">{t("camperTools.desc")}</p>
          <Field id="cableLength" label={t("camperTools.cableLength")} min={0.1} max={50}  step={0.1} />
          <Field id="voltageDrop" label={t("camperTools.maxLoss")}     min={0.1} max={5}   step={0.1} />
          <Select id="material" label={t("camperTools.material")} options={[
            { value: "copper",   label: t("camperTools.copper")    },
            { value: "aluminum", label: t("camperTools.aluminium") },
          ]} />
          <Select id="crossSection" label={t("camperTools.crossSection")} options={[
            { value: 0.75, label: "0.75" }, { value: 1,   label: "1"   },
            { value: 1.5,  label: "1.5"  }, { value: 2.5, label: "2.5" },
            { value: 4,    label: "4"    }, { value: 6,   label: "6"   },
            { value: 10,   label: "10"   }, { value: 16,  label: "16"  },
            { value: 25,   label: "25"   }, { value: 35,  label: "35"  },
            { value: 50,   label: "50"   },
          ]} />
          <Field id="voltage" label={t("camperTools.voltage")} min={5}  max={500} step={1} />
          <Field id="current" label={t("camperTools.current")} min={0}  max={200} step={1} />
        </div>

        <div className="tool-card camper-results-card">
          <h3>{t("camperTools.resultTitle")}</h3>
          <div className="result">
            <strong>{t("camperTools.power")}: </strong>
            <span>{results.power.toFixed(2)} W</span><br />
            <strong>{t("camperTools.voltageLoss")}: </strong>
            <span>{results.actualVoltageDrop.toFixed(2)} %</span><br />
            <strong>{t("camperTools.meetsReq")}: </strong>
            <span className={results.meetsRequirement ? "success" : "error"}>
              {results.meetsRequirement ? t("camperTools.yes") : t("camperTools.no")}
            </span><br />
            <strong>{t("camperTools.tempRise")}: </strong>
            <span>{results.temperatureRaise.toFixed(2)} K</span><br />
            <strong>{t("camperTools.cableTemp")}: </strong>
            <span>{results.cableTemp.toFixed(2)} °C</span>
          </div>
        </div>
      </div>

      <div className="camper-resources">
        <div className="sub2head">{t("camperTools.resourcesTitle")}</div>
        <span className="section-label">{t("camperTools.langNote")}</span>

        <div className="camper-resource-cards">
          <div className="tool-card camper-resource-card">
            <div className="sub3head">{t("camperTools.solarTitle")}</div>
            <ul className="resource-list">
              {t("camperTools.solarVideos").map((v, i) => (
                <li key={i}>
                  <a href={v.url} target="_blank" rel="noreferrer" className="link resource-link">
                    <YtIcon />{v.title}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="tool-card camper-resource-card">
            <div className="sub3head">{t("camperTools.sogTitle")}</div>
            <p className="calc-desc">{t("camperTools.sogDesc")}</p>
            <ul className="resource-list">
              {t("camperTools.sogVideos").map((v, i) => (
                <li key={i}>
                  <a href={v.url} target="_blank" rel="noreferrer" className="link resource-link">
                    <YtIcon />{v.title}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="tool-card camper-resource-card">
            <div className="sub3head">{t("camperTools.camperMonitorTitle")}</div>
            <p className="calc-desc">{t("camperTools.camperMonitorSubline")}</p>
            <Link to="/camper-monitor" className="link resource-link">
              {t("camperTools.camperMonitorRef")} Camper Monitor
            </Link>
          </div>
        </div>

        <div className="tool-card camper-blog-card">
          <div className="sub2head">{t("camperTools.solarBlogTitle")}</div>
          <div className="blog-content">
            <BlogContent />
          </div>
        </div>
      </div>
    </div>
  );
}

export default CamperTools;
